import type { PMReportFrequencyFilter, PMReportExcludableStatus } from './reportService';
import { resolveQuickPeriod } from '../pages/Reports/lib/quickDateRange';
import type { QuickPeriod } from '../pages/Reports/lib/quickDateRange';

export type DateRule =
    | { kind: 'rolling'; preset: QuickPeriod }
    | { kind: 'absolute'; startDate: string; endDate: string };

export interface PMReportPreset {
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    projectIds: string[];
    frequency: PMReportFrequencyFilter;
    excludeStatuses: PMReportExcludableStatus[];
    outputFormat: 'excel' | 'pdf';
    dateRule: DateRule;
}

const PRESETS_KEY = 'pm-report-presets';
export const PRESETS_MAX = 20;

export class PresetLimitError extends Error {
    constructor() { super('Preset limit reached'); this.name = 'PresetLimitError'; }
}
export class DuplicateNameError extends Error {
    constructor(name: string) { super(`A preset named "${name}" already exists`); this.name = 'DuplicateNameError'; }
}
export class StorageWriteError extends Error {
    constructor(cause: unknown) { super('Could not write to local storage'); this.name = 'StorageWriteError'; (this as any).cause = cause; }
}

function readArray<T>(key: string): T[] {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed as T[] : [];
    } catch {
        return [];
    }
}

function writeArray<T>(key: string, value: T[]): void {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
        throw new StorageWriteError(err);
    }
}

export function loadPresets(): PMReportPreset[] {
    return readArray<PMReportPreset>(PRESETS_KEY);
}

export function savePreset(
    input: Omit<PMReportPreset, 'id' | 'createdAt' | 'updatedAt'>
): PMReportPreset {
    const all = loadPresets();
    const trimmedName = input.name.trim();
    if (!trimmedName) throw new Error('Preset name is required');
    if (trimmedName.length > 40) throw new Error('Preset name must be 40 characters or fewer');
    if (all.some(p => p.name.toLowerCase() === trimmedName.toLowerCase())) {
        throw new DuplicateNameError(trimmedName);
    }
    if (all.length >= PRESETS_MAX) throw new PresetLimitError();

    const now = new Date().toISOString();
    const preset: PMReportPreset = {
        ...input,
        name: trimmedName,
        id: crypto.randomUUID(),
        createdAt: now,
        updatedAt: now,
    };
    writeArray(PRESETS_KEY, [...all, preset]);
    return preset;
}

export function updatePreset(id: string, patch: Partial<Omit<PMReportPreset, 'id' | 'createdAt'>>): PMReportPreset {
    const all = loadPresets();
    const idx = all.findIndex(p => p.id === id);
    if (idx < 0) throw new Error(`Preset ${id} not found`);

    if (patch.name !== undefined) {
        const trimmed = patch.name.trim();
        if (!trimmed) throw new Error('Preset name is required');
        if (trimmed.length > 40) throw new Error('Preset name must be 40 characters or fewer');
        const conflict = all.some(p => p.id !== id && p.name.toLowerCase() === trimmed.toLowerCase());
        if (conflict) throw new DuplicateNameError(trimmed);
        patch = { ...patch, name: trimmed };
    }

    const updated: PMReportPreset = {
        ...all[idx],
        ...patch,
        id: all[idx].id,
        createdAt: all[idx].createdAt,
        updatedAt: new Date().toISOString(),
    };
    const next = [...all];
    next[idx] = updated;
    writeArray(PRESETS_KEY, next);
    return updated;
}

export function deletePreset(id: string): void {
    const all = loadPresets();
    const next = all.filter(p => p.id !== id);
    if (next.length === all.length) return; // no-op
    writeArray(PRESETS_KEY, next);
}

export function reorderPresets(orderedIds: string[]): void {
    const all = loadPresets();
    const byId = new Map(all.map(p => [p.id, p]));
    const seen = new Set<string>();
    const result: PMReportPreset[] = [];
    for (const id of orderedIds) {
        const p = byId.get(id);
        if (p && !seen.has(id)) { result.push(p); seen.add(id); }
    }
    for (const p of all) {
        if (!seen.has(p.id)) result.push(p);
    }
    writeArray(PRESETS_KEY, result);
}

export interface PMReportLastUsed {
    projectIds: string[];
    frequency: PMReportFrequencyFilter;
    excludeStatuses: PMReportExcludableStatus[];
    outputFormat: 'excel' | 'pdf';
    dateRule: DateRule;
    savedAt: string;
}

const LAST_USED_KEY = 'pm-report-last-used';
const LAST_USED_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

export function loadLastUsed(): PMReportLastUsed | null {
    try {
        const raw = localStorage.getItem(LAST_USED_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PMReportLastUsed;
        if (!parsed || typeof parsed !== 'object' || !parsed.savedAt) return null;
        const ageMs = Date.now() - new Date(parsed.savedAt).getTime();
        if (!Number.isFinite(ageMs) || ageMs > LAST_USED_MAX_AGE_MS) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function saveLastUsed(snapshot: Omit<PMReportLastUsed, 'savedAt'>): void {
    const full: PMReportLastUsed = { ...snapshot, savedAt: new Date().toISOString() };
    try {
        localStorage.setItem(LAST_USED_KEY, JSON.stringify(full));
    } catch (err) {
        throw new StorageWriteError(err);
    }
}

export function resolveDateRule(rule: DateRule, now: Date): { startDate: string; endDate: string } {
    if (rule.kind === 'absolute') {
        return { startDate: rule.startDate, endDate: rule.endDate };
    }
    return resolveQuickPeriod(rule.preset, now);
}

interface ProjectIdHolder { id: string }

export function filterAvailableProjects<P extends ProjectIdHolder>(
    preset: { projectIds: string[] },
    available: P[]
): { kept: string[]; missing: string[] } {
    const availableSet = new Set(available.map(p => p.id));
    const kept: string[] = [];
    const missing: string[] = [];
    for (const id of preset.projectIds) {
        if (availableSet.has(id)) kept.push(id);
        else missing.push(id);
    }
    return { kept, missing };
}
